/**
 * Performance Hooks for React 19
 * Optimized for production performance monitoring and optimization
 */

import { useCallback, useEffect, useRef, useState, useMemo } from 'react';

/**
 * Debounce hook - prevents excessive function calls
 * Use for: search inputs, resize handlers, scroll events
 */
export function useDebounce<T>(value: T, delay: number = 500): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

/**
 * Throttle hook - limits function execution rate
 * Use for: analytics events, scroll handlers, frequent updates
 */
export function useThrottle<T>(value: T, limit: number = 300): T {
  const [throttledValue, setThrottledValue] = useState<T>(value);
  const lastRan = useRef<number>(Date.now());

  useEffect(() => {
    const handler = setTimeout(() => {
      if (Date.now() - lastRan.current >= limit) {
        setThrottledValue(value);
        lastRan.current = Date.now();
      }
    }, limit - (Date.now() - lastRan.current));

    return () => {
      clearTimeout(handler);
    };
  }, [value, limit]);

  return throttledValue;
}

/**
 * Intersection Observer hook - for lazy loading and visibility tracking
 * Use for: lazy loading images, infinite scroll, analytics tracking
 */
export function useIntersectionObserver(
  ref: React.RefObject<Element>,
  options: IntersectionObserverInit = {}
) {
  const [isIntersecting, setIntersecting] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(([entry]) => {
      if (entry) {
        setIntersecting(entry.isIntersecting);
      }
    }, options);

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [ref, options]);

  return isIntersecting;
}

/**
 * Event listener hook with automatic cleanup
 * Use for: window events, document events, custom events
 */
export function useEventListener<K extends keyof WindowEventMap>(
  eventName: K,
  handler: (event: WindowEventMap[K]) => void,
  element: Window | HTMLElement = window
) {
  const savedHandler = useRef(handler);

  useEffect(() => {
    savedHandler.current = handler;
  }, [handler]);

  useEffect(() => {
    const isSupported = element && element.addEventListener;
    if (!isSupported) return;

    const eventListener = (event: Event) =>
      savedHandler.current(event as WindowEventMap[K]);

    element.addEventListener(eventName, eventListener);

    return () => {
      element.removeEventListener(eventName, eventListener);
    };
  }, [eventName, element]);
}

/**
 * Previous value hook - for comparing prop changes
 * Use for: preventing unnecessary re-renders, comparing state changes
 */
export function usePrevious<T>(value: T): T | undefined {
  const ref = useRef<T>();

  useEffect(() => {
    ref.current = value;
  }, [value]);

  return ref.current;
}

/**
 * Media query hook - for responsive behavior
 * Use for: conditional rendering based on screen size
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    const media = window.matchMedia(query);

    if (media.matches !== matches) {
      setMatches(media.matches);
    }

    const listener = () => setMatches(media.matches);
    media.addEventListener('change', listener);

    return () => media.removeEventListener('change', listener);
  }, [matches, query]);

  return matches;
}

/**
 * Performance monitor hook - tracks component render performance
 * Use for: identifying slow components, production monitoring
 */
export function usePerformanceMonitor(componentName: string) {
  const renderCount = useRef(0);
  const startTime = useRef<number>(performance.now());

  useEffect(() => {
    renderCount.current += 1;
    const duration = performance.now() - startTime.current;

    if (process.env.NODE_ENV === 'development') {
      console.log(`[Performance] ${componentName}:`, {
        renderCount: renderCount.current,
        duration: `${duration.toFixed(2)}ms`,
      });
    }

    // Send to analytics in production
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'component_render', {
        component: componentName,
        duration,
        render_count: renderCount.current,
      });
    }
  });
}

/**
 * Lazy load images hook - optimizes image loading
 * Use for: feature cards, testimonial images, gallery images
 */
export function useLazyImage(src: string, placeholder: string = '') {
  const [imageSrc, setImageSrc] = useState(placeholder);
  const [imageLoaded, setImageLoaded] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const img = imgRef.current;
    if (!img) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const image = new Image();
            image.src = src;
            image.onload = () => {
              setImageSrc(src);
              setImageLoaded(true);
            };
            observer.disconnect();
          }
        });
      },
      { rootMargin: '50px' }
    );

    observer.observe(img);

    return () => {
      observer.disconnect();
    };
  }, [src]);

  return { imageSrc, imageLoaded, imgRef };
}

/**
 * Optimized callback hook - memoizes callbacks with deep comparison
 * Use for: complex callbacks passed to child components
 */
export function useOptimizedCallback<T extends (...args: any[]) => any>(
  callback: T,
  dependencies: any[]
): T {
  const callbackRef = useRef<T>(callback);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  return useCallback(
    ((...args: any[]) => callbackRef.current(...args)) as T,
    dependencies
  );
}

/**
 * Memoized value hook with equality check
 * Use for: expensive calculations, filtered/sorted data
 */
export function useDeepMemo<T>(factory: () => T, deps: any[]): T {
  const ref = useRef<{ value: T; deps: any[] }>();

  if (!ref.current || !areEqual(ref.current.deps, deps)) {
    ref.current = { value: factory(), deps };
  }

  return ref.current.value;
}

// Helper function for deep equality check
function areEqual(a: any[], b: any[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((val, index) => {
    if (typeof val === 'object' && val !== null) {
      return JSON.stringify(val) === JSON.stringify(b[index]);
    }
    return val === b[index];
  });
}

/**
 * Idle callback hook - defers non-critical work
 * Use for: analytics, logging, non-urgent updates
 */
export function useIdleCallback(callback: () => void, dependencies: any[]) {
  useEffect(() => {
    if ('requestIdleCallback' in window) {
      const handle = requestIdleCallback(callback);
      return () => cancelIdleCallback(handle);
    } else {
      // Fallback for browsers without requestIdleCallback
      const timeout = setTimeout(callback, 1);
      return () => clearTimeout(timeout);
    }
  }, dependencies);
}

/**
 * Analytics tracking hook with debouncing
 * Use for: tracking user interactions, page views, events
 */
export function useAnalytics() {
  const trackEvent = useCallback(
    (eventName: string, eventData?: Record<string, any>) => {
      if (typeof window === 'undefined') return;

      // Defer analytics to avoid blocking main thread
      requestIdleCallback(() => {
        // Google Analytics
        if ((window as any).gtag) {
          (window as any).gtag('event', eventName, eventData);
        }

        // Custom analytics
        if ((window as any).analytics) {
          (window as any).analytics.track(eventName, eventData);
        }

        if (process.env.NODE_ENV === 'development') {
          console.log('[Analytics]', eventName, eventData);
        }
      });
    },
    []
  );

  const trackPageView = useCallback((path: string, title?: string) => {
    trackEvent('page_view', { page_path: path, page_title: title });
  }, [trackEvent]);

  return { trackEvent, trackPageView };
}

/**
 * Optimized scroll hook - prevents excessive scroll event handling
 * Use for: scroll-based animations, sticky headers, progress indicators
 */
export function useOptimizedScroll(callback: (scrollY: number) => void) {
  const [scrollY, setScrollY] = useState(0);
  const ticking = useRef(false);

  useEffect(() => {
    const handleScroll = () => {
      if (!ticking.current) {
        window.requestAnimationFrame(() => {
          const currentScrollY = window.scrollY;
          setScrollY(currentScrollY);
          callback(currentScrollY);
          ticking.current = false;
        });
        ticking.current = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [callback]);

  return scrollY;
}
